import { useServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/useServerSideDataGridModel.tsx";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid.tsx";
import {
	dateColumn,
	dateTimeColumn,
	enumColumn,
	integerColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import { getDeliveryStateLabel, getDeliveryStateLabels } from "src/api/generated/erp/db/types/enums/deliveryState.ts";
import i18n from "i18next";
import { formatDayJs } from "src/utils/dayjsUtils.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTheme } from "@mui/material";
import { faBoxes, faCheck, faHourglassHalf, faImage, faTruck, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import {
	DeliveryForm,
	DeliveryFormNewDeliveryDefaults,
} from "src/components/views/erp/delivery/delivery/DeliveryForm.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { DeliveryActionApi } from "src/api/generated/erp/delivery/api/deliveryActionApi.ts";
import { DeliveriesDataGridApi_DeliveryDto } from "src/api/generated/erp/delivery/api/deliveriesDataGridApi.ts";
import { DeliveryEditApi } from "src/api/generated/erp/delivery/api/deliveryEditApi.ts";
import { DeliveryFormWithPackages } from "src/components/views/erp/delivery/delivery/DeliveryFormWithPackages.tsx";
import {
	PrintDeliveryConsignmentNoteButton,
	PrintDeliveryNoteButton,
	PrintDeliveryPackingSlipButton,
} from "src/components/views/erp/delivery/delivery/commonDeliveryComponents.tsx";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox";
import { ServerSideDataModelResult } from "src/api/generated/common/dataGrids/serverSideDataModelResult.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import type { AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import {
	AavoDataGridMobileViewDetailsViewRenderParams,
	AavoDataGridMobileViewDetailsViewTabsConfig,
	AavoDataGridRowContextMenuParams,
	AavoGridColDef,
} from "src/components/common/dataGrid/types";
import { ParamsWithDataModelRequest } from "src/components/common/dataGrid/gridModel/ServerSideDataGridModel";
import { getOnlyMember } from "src/utils/arrayUtils.ts";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { TypedGridSortModel } from "src/components/common/dataGrid/types.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import React from "react";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { DeliveryPackagesDataGrid } from "src/components/views/erp/delivery/deliveryPackage/DeliveryPackagesDataGrid.tsx";
import { concatWithPipe } from "src/utils/strings.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { ImageView } from "src/components/views/documents/preview/ImageView.tsx";
import { DeliveryFormWithCargoDriver } from "src/components/views/erp/delivery/delivery/DeliveryFormForCargoDriver.tsx";
import { getCountryLabels } from "src/components/common/country/countries.ts";
import { TextWithPhoneLinks } from "src/components/common/dataGrid/cells/TextWithPhoneLinks.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DeliveriesDataGridBaseProps {
	fetchData: (
		params: ParamsWithDataModelRequest<FetchDataParams>,
	) => Promise<ServerSideDataModelResult<DeliveriesDataGridApi_DeliveryDto>>;
	refreshRef?: RefreshableElementRef;
	gridId: string;
	siteId?: number;
	onSelectionChanged?: (selectedDelivery: DeliveriesDataGridApi_DeliveryDto | undefined) => void;
	showSearchField?: boolean;
	showOnlyUndeliveredByDefault?: boolean;
	showCustomerOrderButtons?: boolean;
	newDeliveryDefaults?: DeliveryFormNewDeliveryDefaults;
	onDeliveryStateChanged?: () => Promise<unknown>;
	defaultSortModel?: TypedGridSortModel<DeliveriesDataGridApi_DeliveryDto>;
	getDetailPanelContent?: AavoDataGridProps<DeliveriesDataGridApi_DeliveryDto>["getDetailPanelContent"];
	disableDelete?: boolean;
	extraColumns?: AavoGridColDef<DeliveriesDataGridApi_DeliveryDto>[];
	rowContextMenuExtraComponents?: (
		params: AavoDataGridRowContextMenuParams<DeliveriesDataGridApi_DeliveryDto>,
	) => React.ReactNode;
	actionBarExtraComponents?: (params: { onlySelectedRow?: DeliveriesDataGridApi_DeliveryDto }) => React.ReactNode;
	/** Prints, marks delivered and edits through the cargo driver API, limited to the driver's own transports. */
	cargoDriverView?: boolean;
}

interface FetchDataParams {
	searchQuery: string;
	onlyUndelivered: boolean;
	siteId: number;
}

export const DeliveriesDataGridBase = ({
	fetchData,
	refreshRef,
	gridId,
	onSelectionChanged,
	showSearchField,
	showOnlyUndeliveredByDefault,
	showCustomerOrderButtons = false,
	extraColumns = [],
	newDeliveryDefaults,
	onDeliveryStateChanged,
	siteId: siteIdProp,
	defaultSortModel,
	getDetailPanelContent,
	disableDelete = false,
	rowContextMenuExtraComponents,
	actionBarExtraComponents,
	cargoDriverView = false,
}: DeliveriesDataGridBaseProps) => {
	const { defaultSiteId } = useGlobalInitData();

	const { dataGridProps, refreshData, currentParams, onlySelectedRow } = useServerSideDataGridModel({
		gridId: gridId + "_v2",
		fetchData: fetchData,
		initialParams: {
			searchQuery: "",
			onlyUndelivered: showOnlyUndeliveredByDefault ?? false,
			siteId: siteIdProp ?? defaultSiteId,
		},
		getRowId: (row) => row.deliveryId,
		customFunctionUiActionView: "DELIVERIES",
		onSelectionChanged: (selectedDeliveries) => onSelectionChanged?.(selectedDeliveries[0]),
		selectFirstRowOnLoad: true,
		refreshRef: refreshRef,
		defaultGridState: {
			sorting: {
				sortModel: defaultSortModel,
			},
		},
	});

	const { palette } = useTheme();

	const hasPermission = useHasPermission();
	const editDeliveriesPermission = hasPermission(Permissions.Delivery.Deliveries.MANAGE);
	const readCustomerOrdersPermission = hasPermission(Permissions.Sales.CustomerOrders.READ);
	const changeDeliveryStateEnabled = cargoDriverView || editDeliveriesPermission;

	return (
		<ControlledAsyncFormCrudDataGrid<DeliveriesDataGridApi_DeliveryDto>
			disableMultipleRowSelection
			columns={[
				integerColumn({
					field: "deliveryId",
					headerName: i18n.t("number_shortened"),
					width: 80,
				}),
				enumColumn({
					field: "deliveryState",
					headerName: i18n.t("status"),
					enumLabels: getDeliveryStateLabels(),
					mobile: "cell",
					renderCell: ({ row }) => (
						<HorizontalBox sx={{ alignItems: "center", gap: 1 }}>
							<FontAwesomeIcon
								icon={row.deliveryState === "DELIVERED" ? faCheck : faHourglassHalf}
								color={row.deliveryState === "DELIVERED" ? palette.success.main : palette.grey[600]}
							/>
							{getDeliveryStateLabel(row.deliveryState)}
						</HorizontalBox>
					),
				}),
				dateColumn({
					field: "actualDeliveryDate",
					headerName: i18n.t("delivered_at"),
				}),
				textColumn({
					field: "deliveryMethodCode",
					headerName: i18n.t("delivery_method"),
				}),
				textColumn({
					field: "cargoDriverName",
					headerName: i18n.t("driver"),
				}),
				dateTimeColumn({
					field: "plannedLoadingDate",
					headerName: i18n.t("loading.logistics"),
					width: 160,
					valueFormatter: (value) => formatDayJs(value, `L LT [(${i18n.t("week_abbr")}] w)`),
					mobile: "cell",
				}),
				dateTimeColumn({
					field: "plannedUnloadingDate",
					headerName: i18n.t("unloading"),
					width: 160,
					mobile: "cell",
				}),
				textColumn({
					field: "trackingCode",
					headerName: i18n.t("tracking_code"),
					width: 130,
				}),
				textColumn({
					field: "note",
					headerName: i18n.t("note"),
					width: 130,
				}),
				textColumn({
					field: "deliveryName",
					headerName: i18n.t("address_description"),
					width: 130,
				}),
				textColumn({
					field: "deliveryAddress_1",
					headerName: i18n.t("street_address"),
					width: 150,
					mobile: "cell",
				}),
				textColumn({
					field: "deliveryPostalCode",
					headerName: i18n.t("postal_code"),
				}),
				textColumn({
					field: "deliveryCity",
					headerName: i18n.t("city"),
					mobile: "cell",
				}),
				enumColumn({
					field: "deliveryCountry",
					headerName: i18n.t("country"),
					enumLabels: getCountryLabels(),
				}),
				textColumn({
					field: "senderContact",
					headerName: i18n.t("sender_contact"),
					width: 170,
					renderCell: ({ value }) => <TextWithPhoneLinks text={value} />,
				}),
				textColumn({
					field: "recipientContact",
					headerName: i18n.t("recipient_contact"),
					width: 180,
					renderCell: ({ value }) => <TextWithPhoneLinks text={value} />,
				}),
				dateTimeColumn({
					field: "createdDate",
					headerName: i18n.t("created_at"),
					width: 140,
				}),
				textColumn({
					field: "createdByUserName",
					headerName: i18n.t("created_by"),
				}),
				textColumn({
					field: "customerOrderDeliveryNotes" as any,
					filterable: false,
					sortable: false,
					headerName: i18n.t("delivery_notes"),
					valueGetter: (_, row) =>
						row.connectedCustomerOrders
							.map((order) => order.deliveryNotes)
							.filter((note) => note != null && note.trim() !== "")
							.join(", "),
					mobile: "cell",
				}),
				...extraColumns,
			]}
			listViewOnMobile={{
				header: (delivery) => {
					const customerOrder = getOnlyMember(delivery.connectedCustomerOrders);
					return customerOrder != null ?
							concatWithPipe(customerOrder.customerOrderId, customerOrder.orderReference)
						:	delivery.note;
				},
				detailsView: (params) => MobileViewDetailsView({ ...params, cargoDriverView }),
			}}
			actionBarComponents={
				<>
					<DocumentsOfObjectButton
						objectRef={nullableAavoObjectRef("DELIVERY", onlySelectedRow?.deliveryId)}
					/>
					{showSearchField && (
						<AavoTextField
							label={i18n.t("search")}
							onSubmit={(searchQuery) => refreshData({ searchQuery })}
						/>
					)}
					{showOnlyUndeliveredByDefault && (
						<AavoCheckbox
							label={i18n.t("show_delivered")}
							checked={!currentParams.onlyUndelivered}
							onChange={(value) => refreshData({ onlyUndelivered: !value })}
						/>
					)}
					{actionBarExtraComponents?.({ onlySelectedRow })}
				</>
			}
			rowContextMenuComponents={async (contextMenuParams) => {
				const { row, onlySingleRowSelected } = contextMenuParams;
				const onlyConnectedCustomerOrder = getOnlyMember(row.connectedCustomerOrders);
				return [
					showCustomerOrderButtons &&
						readCustomerOrdersPermission &&
						onlyConnectedCustomerOrder && [
							<OpenCustomerOrderButton
								key={"openCustomerOrder"}
								customerOrderId={onlyConnectedCustomerOrder.customerOrderId}
								variant={"menu"}
							/>,
							<DocumentsOfObjectButton
								key={"openCustomerOrderDocuments"}
								label={i18n.t("order_documents")}
								variant={"menu"}
								objectRef={{
									objectType: "CUSTOMER_ORDER",
									objectId: onlyConnectedCustomerOrder.customerOrderId,
								}}
							/>,
						],
					<PrintDeliveryConsignmentNoteButton
						key={"printConsignmentNote"}
						variant={"menu"}
						delivery={row}
						cargoDriverView={cargoDriverView}
					/>,
					<PrintDeliveryPackingSlipButton
						key={"printPackingSlip"}
						variant={"menu"}
						delivery={row}
						cargoDriverView={cargoDriverView}
					/>,
					<PrintDeliveryNoteButton
						key={"printDeliveryNote"}
						variant={"menu"}
						delivery={row}
						cargoDriverView={cargoDriverView}
					/>,
					changeDeliveryStateEnabled && onlySingleRowSelected && row?.deliveryState === "INITIAL" && (
						<AsyncMenuButton
							key={"markToDelivered"}
							icon={faTruck}
							label={i18n.t("mark_delivered")}
							onClick={async () => {
								if (cargoDriverView)
									await DeliveryActionApi.setDeliveryToDeliveredForCargoDriver({
										deliveryId: row.deliveryId,
									});
								else await DeliveryActionApi.setDeliveryToDelivered({ deliveryId: row.deliveryId });
								await Promise.all([refreshData(), onDeliveryStateChanged?.()]);
							}}
						/>
					),
					changeDeliveryStateEnabled && onlySingleRowSelected && row?.deliveryState === "DELIVERED" && (
						<AsyncMenuButton
							key={"markToUndelivered"}
							icon={faUndo}
							label={i18n.t("return_to_initial_state")}
							onClick={async () => {
								if (cargoDriverView)
									await DeliveryActionApi.revertDeliveryToInitialForCargoDriver({
										deliveryId: row.deliveryId,
									});
								else await DeliveryActionApi.revertDeliveryToInitial({ deliveryId: row.deliveryId });
								await Promise.all([refreshData(), onDeliveryStateChanged?.()]);
							}}
						/>
					),
					rowContextMenuExtraComponents?.(contextMenuParams),
				];
			}}
			form={{
				addRowEnabled: !cargoDriverView && editDeliveriesPermission && newDeliveryDefaults != null,
				editEnabled: cargoDriverView || editDeliveriesPermission,
				dialogTitle: i18n.t("delivery"),
				dialogSize: (row) => (row == null ? "xl" : "fullscreen"),
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.delivery.delivery.formDialog"),
					pathSegments: row == null ? [i18n.t("delivery")] : [`${i18n.t("delivery")} ${row.deliveryId}`],
					data: {
						deliveryId: row?.deliveryId ?? null,
						siteId: row?.siteId ?? currentParams.siteId,
					},
				}),
				component: ({ row, onCompleted, onFormEdited }) => {
					if (cargoDriverView) {
						if (row == null) return null;
						return (
							<DeliveryFormWithCargoDriver
								deliveryId={row.deliveryId}
								onCompleted={onCompleted}
								onFormEdited={onFormEdited}
							/>
						);
					}
					if (editDeliveriesPermission) {
						if (row == null) {
							if (newDeliveryDefaults == null) return;
							return (
								<DeliveryForm
									onCompleted={onCompleted}
									onFormEdited={onFormEdited}
									initialization={{
										type: "newDelivery",
										...newDeliveryDefaults,
									}}
								/>
							);
						} else {
							return (
								<DeliveryFormWithPackages
									deliveryId={row.deliveryId}
									onFormCompleted={onCompleted}
									onFormEdited={onFormEdited}
									readonly={!editDeliveriesPermission}
								/>
							);
						}
					}
					return null;
				},
			}}
			remove={
				!cargoDriverView &&
				editDeliveriesPermission &&
				!disableDelete &&
				(({ items }) =>
					DeliveryEditApi.delete_({
						deliveryIds: items.map((item) => item.deliveryId),
					}))
			}
			getDetailPanelContent={getDetailPanelContent}
			agentContext={{
				key: defineAgentUiContextKey("erp.delivery.deliveries.grid"),
				data: { siteId: currentParams.siteId },
				rowFields: ["deliveryId", "siteId", "trackingCode"],
				selectedRowPathSegment: (row) => `${i18n.t("delivery")} ${row.deliveryId}`,
			}}
			{...dataGridProps}
		/>
	);
};

interface MobileViewDetailsViewProps extends AavoDataGridMobileViewDetailsViewRenderParams<DeliveriesDataGridApi_DeliveryDto> {
	cargoDriverView: boolean;
}

const MobileViewDetailsView = ({
	row,
	cargoDriverView,
}: MobileViewDetailsViewProps): AavoDataGridMobileViewDetailsViewTabsConfig => {
	const firstCustomerOrder = row.connectedCustomerOrders[0];
	const imageUrl = row.imageUrl ?? firstCustomerOrder?.imageUrl;

	return {
		type: "tabs",
		items: [
			imageUrl != null && {
				title: i18n.t("image"),
				icon: faImage,
				content: <ImageView fileUrl={imageUrl} />,
			},
			{
				title: i18n.t("packages"),
				icon: faBoxes,
				content: (
					<DeliveryPackagesDataGrid
						gridId={"215795496EC3817C"}
						deliveryId={row.deliveryId}
						disablePagination
						cargoDriverView={cargoDriverView}
					/>
				),
			},
		],
	};
};
